'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { InputContainer, InputWrapper } from '../InputContainer';
import { Button, Icon } from '@benzinga/core-ui';
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { formatPrice } from '@benzinga/utils';

interface Expense {
  currentAge: number;
  lifespan: number;
  name: string;
  replacementCost: number;
}

interface ExpensesTotal {
  annualBudget: number;
  costPerMonth: number;
  costPerYear: number;
  monthlyBudget: number;
  replacementCost: number;
}

interface ExpensesResult {
  annualBudget: number;
  costPerMonth: number;
  costPerYear: number;
  monthlyBudget: number;
  remainingLife: number;
  currentAge: number;
  lifespan: number;
  name: string;
  replacementCost: number;
}

const initialNewExpense = {
  currentAge: 0,
  lifespan: 0,
  name: 'Untitled',
  replacementCost: 0,
};

const initialExpenses = [
  {
    currentAge: 0,
    lifespan: 0,
    name: 'Roof',
    replacementCost: 0,
  },
  {
    currentAge: 0,
    lifespan: 0,
    name: 'Windows',
    replacementCost: 0,
  },
  {
    currentAge: 0,
    lifespan: 0,
    name: 'HVAC',
    replacementCost: 0,
  },
  {
    currentAge: 0,
    lifespan: 0,
    name: 'Water Heater',
    replacementCost: 0,
  },
  {
    currentAge: 0,
    lifespan: 0,
    name: 'All Appliances',
    replacementCost: 0,
  },
  {
    currentAge: 0,
    lifespan: 0,
    name: 'Flooring',
    replacementCost: 0,
  },
  {
    currentAge: 0,
    lifespan: 0,
    name: 'Bathroom',
    replacementCost: 0,
  },
  {
    currentAge: 0,
    lifespan: 0,
    name: 'Cabinets/Counters',
    replacementCost: 0,
  },
  {
    currentAge: 0,
    lifespan: 0,
    name: 'Plumbing',
    replacementCost: 0,
  },
  {
    currentAge: 0,
    lifespan: 0,
    name: 'Driveway/Parking Lot',
    replacementCost: 0,
  },
  {
    currentAge: 0,
    lifespan: 0,
    name: 'Structure/Foundation',
    replacementCost: 0,
  },
  {
    currentAge: 0,
    lifespan: 0,
    name: 'Components',
    replacementCost: 0,
  },
];
export interface CapitalExpenseCalculatorProps {
  variant?: 'default' | 'compact';
}

export const CapitalExpenseCalculator: React.FC<CapitalExpenseCalculatorProps> = ({ variant = 'default' }) => {
  const [expenses, setExpenses] = React.useState<Expense[]>(initialExpenses);
  const [newExpense, setNewExpense] = React.useState<Expense>(initialNewExpense);
  const [capitalExpensesResults, setCapitalExpensesResults] = React.useState<ExpensesResult[]>();
  const [expenseTotals, setExpenseTotals] = React.useState<ExpensesTotal>();
  const expenseResultsTableRef = React.useRef(null);

  const calculate = React.useCallback((newExpenses: Expense[]): void => {
    const newTotals: ExpensesTotal = {
      annualBudget: 0,
      costPerMonth: 0,
      costPerYear: 0,
      monthlyBudget: 0,
      replacementCost: 0,
    };

    const expensesFiltered = newExpenses.filter(
      expense => expense.currentAge && expense.lifespan && expense.replacementCost && expense.name,
    );

    const calculations: ExpensesResult[] = expensesFiltered.map(expense => {
      const remainingLife = expense.lifespan - expense.currentAge;
      const costPerYear = expense.replacementCost / expense.lifespan;
      const costPerMonth = costPerYear / 12;
      const annualBudget = expense.replacementCost / remainingLife;
      const monthlyBudget = annualBudget / 12;

      newTotals.costPerYear += costPerYear;
      newTotals.costPerMonth += costPerMonth;
      newTotals.annualBudget += annualBudget;
      newTotals.monthlyBudget += monthlyBudget;
      newTotals.replacementCost += expense.replacementCost;

      const result: ExpensesResult = {
        ...expense,
        annualBudget,
        costPerMonth,
        costPerYear,
        monthlyBudget,
        remainingLife,
      };

      return result;
    });

    setCapitalExpensesResults(calculations);
    setExpenseTotals(newTotals);
  }, []);

  React.useEffect(() => {
    calculate(expenses);
  }, [calculate, expenses]);

  const handleOnChangeReplacementCost = React.useCallback(
    (expense: Expense, value: number) => {
      const nextExpenses = [...expenses];
      const result = nextExpenses.find(item => item.name === expense.name);
      if (result) result.replacementCost = value;
      setExpenses(nextExpenses);
    },
    [expenses],
  );

  const handleOnChangeLifespan = React.useCallback(
    (expense: Expense, value: number) => {
      const nextExpenses = [...expenses];
      const result = nextExpenses.find(item => item.name === expense.name);
      if (result) result.lifespan = value;
      setExpenses(nextExpenses);
    },
    [expenses],
  );

  const handleOnChangeCurrentAge = React.useCallback(
    (expense: Expense, value: number) => {
      const nextExpenses = [...expenses];
      const result = nextExpenses.find(item => item.name === expense.name);
      if (result) result.currentAge = value;
      setExpenses(nextExpenses);
    },
    [expenses],
  );

  const handleOnChangeNewExpense = React.useCallback(
    (name: string, value: string | number) => {
      const updatedExpense = { ...newExpense };

      if (name === 'New Expense') updatedExpense.name = value as string;
      if (name === 'Replacement Cost') updatedExpense.replacementCost = value as number;
      if (name === 'Lifespan') updatedExpense.lifespan = value as number;
      if (name === 'Current Age') updatedExpense.currentAge = value as number;

      setNewExpense(updatedExpense);
    },
    [newExpense],
  );

  const handleAddItem = () => {
    const newExpenses = [...expenses, newExpense];
    setExpenses(newExpenses);
    setNewExpense(initialNewExpense);
  };

  const handleRemoveItem = (value: Expense) => {
    const index = expenses.indexOf(value);
    const newExpenses = [...expenses];
    if (index > -1) {
      newExpenses.splice(index, 1);
      setExpenses(newExpenses);
    }
  };

  const handleReset = () => {
    setExpenses(initialExpenses);
  };

  const handleExportXLS = async () => {
    const tableElement = expenseResultsTableRef.current;
    const { utils, writeFile } = await import('xlsx');
    const wb = utils.table_to_book(tableElement, { raw: true });
    writeFile(wb, `benzinga_capital_expense.xlsx`);
  };

  // const handleExportPDF = () => {

  // };

  const hasNoZerosExpenseTotals =
    expenseTotals &&
    expenseTotals.annualBudget &&
    expenseTotals.costPerMonth &&
    expenseTotals.costPerYear &&
    expenseTotals.monthlyBudget &&
    expenseTotals.replacementCost;

  return (
    <CapitalExpenseCalculatorContainer className={`capital-rate-calculator-container ${variant}`}>
      <div className="expenses-list-wrapper">
        <ul className="expenses-list">
          {expenses.map((expense, index) => (
            <li key={`${expense.name}-${index}`}>
              <div className="expense-name">
                <input
                  defaultValue={expense.name}
                  onChange={e => {
                    expense.name = e.target.value;
                  }}
                />
              </div>
              <div className="inputs-container">
                <InputContainer
                  name="Replacement Cost"
                  onChange={value => handleOnChangeReplacementCost(expense, value)}
                  prefix="$"
                  value={expense.replacementCost}
                />
                <InputContainer
                  name="Lifespan (Years)"
                  onChange={value => handleOnChangeLifespan(expense, value)}
                  value={expense.lifespan}
                />
                <InputContainer
                  name="Current Age"
                  onChange={value => handleOnChangeCurrentAge(expense, value)}
                  value={expense.currentAge}
                />
              </div>
              <Button className="remove-button" onClick={() => handleRemoveItem(expense)} variant="danger">
                <span className="remove-button-text">Remove</span>
                <Icon icon={faTrash} />
              </Button>
            </li>
          ))}
        </ul>
        <div className="add-new-expense-div">
          <InputWrapper name="New Expense">
            <input
              name="New Expense"
              onChange={e => handleOnChangeNewExpense('New Expense', e.target.value)}
              value={newExpense.name}
            />
          </InputWrapper>
          <InputContainer
            name="Replacement Cost"
            onChange={value => handleOnChangeNewExpense('Replacement Cost', value)}
            prefix="$"
            value={newExpense.replacementCost}
          />
          <InputContainer
            name="Lifespan (Years)"
            onChange={value => handleOnChangeNewExpense('Lifespan', value)}
            value={newExpense.lifespan}
          />
          <InputContainer
            name="Current Age"
            onChange={value => handleOnChangeNewExpense('Current Age', value)}
            value={newExpense.currentAge}
          />
          <Button className="add-button" onClick={handleAddItem} variant="success">
            Add Expense
          </Button>
        </div>
      </div>
      <div className="reset-button-container">
        <Button onClick={handleReset} variant="danger">
          <span>Reset</span>
        </Button>
      </div>
      {capitalExpensesResults && capitalExpensesResults.length > 0 && (
        <>
          <div className="table-container">
            <table ref={expenseResultsTableRef}>
              <thead>
                <tr>
                  <th>Capital Expense</th>
                  <th>Remaining Life</th>
                  <th>Cost per Year</th>
                  <th>Cost per Month</th>
                  <th>Annual Budget</th>
                  <th>Monthly Budget</th>
                </tr>
              </thead>
              <tbody>
                {capitalExpensesResults.map((expense, index) => (
                  <tr key={`${expense.name}-${index}`}>
                    <td>{expense.name}</td>
                    <td>{expense.remainingLife}</td>
                    <td>${formatPrice(expense.costPerYear, { fixedPlace: 2, showNearZero: false })}</td>
                    <td>${formatPrice(expense.costPerMonth, { fixedPlace: 2, showNearZero: false })}</td>
                    <td>${formatPrice(expense.annualBudget, { fixedPlace: 2, showNearZero: false })}</td>
                    <td>${formatPrice(expense.monthlyBudget, { fixedPlace: 2, showNearZero: false })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasNoZerosExpenseTotals && (
              <table>
                <thead>
                  <tr>
                    <th>Total</th>
                    <th>Replacement Cost</th>
                    <th>Cost per Year</th>
                    <th>Cost per Month</th>
                    <th>Annual Budget</th>
                    <th>Monthly Budget</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td></td>
                    <td>${formatPrice(expenseTotals.replacementCost, { fixedPlace: 2, showNearZero: false })}</td>
                    <td>${formatPrice(expenseTotals.costPerYear, { fixedPlace: 2, showNearZero: false })}</td>
                    <td>${formatPrice(expenseTotals.costPerMonth, { fixedPlace: 2, showNearZero: false })}</td>
                    <td>${formatPrice(expenseTotals.annualBudget, { fixedPlace: 2, showNearZero: false })}</td>
                    <td>${formatPrice(expenseTotals.monthlyBudget, { fixedPlace: 2, showNearZero: false })}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
          <div className="export-buttons-container">
            <Button onClick={handleExportXLS} variant="primary">
              <span>Export XLS</span>
            </Button>
            {/* <Button onClick={handleExportPDF} variant="primary">
              <span>Export PDF</span>
            </Button> */}
          </div>
        </>
      )}
    </CapitalExpenseCalculatorContainer>
  );
};

const CapitalExpenseCalculatorContainer = styled.div`
  &.capital-rate-calculator-container {
    .expenses-list {
      display: flex;
      flex-direction: column;

      li {
        display: flex;
        align-items: flex-end;
        margin-bottom: 1rem;
        height: 60px;

        .inputs-container {
          display: flex;
          flex-direction: row;
          align-items: flex-end;
          padding: 0;
          width: 100%;
        }

        .input-container {
          width: 160px;
          margin-right: 10px;
          padding: 0;

          input {
            height: 30px;
          }

          label {
            margin-bottom: 2px;
            font-size: ${({ theme }) => theme.fontSize.base};
            height: 100%;
          }
        }

        .expense-name {
          background-color: ${({ theme }) => theme.colorPalette.gray600};
          color: ${({ theme }) => theme.colorPalette.white};
          padding: 0 10px;
          height: 30px;
          min-width: 180px;
          width: 180px;
          display: flex;
          align-items: center;
          white-space: nowrap;
          margin-right: 10px;

          input {
            background-color: transparent;

            &:focus {
              outline: none;
            }
          }
        }

        .remove-button {
          height: 30px;
          color: ${({ theme }) => theme.colorPalette.red600};
          margin-left: 10px;
        }

        .remove-button-text {
          display: none;
        }
      }
    }

    .add-new-expense-div {
      margin: 1rem 0;
      padding: 1rem 0;
      border-top: 1px solid ${({ theme }) => theme.colors.border};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};

      .input-container {
        margin-bottom: 0.5rem;

        &:last-of-type {
          margin-bottom: 0;
        }
      }

      .add-button {
        margin-top: 0.5rem;
        width: 100%;
      }
    }

    .error-message {
      color: ${({ theme }) => theme.colorPalette.red500};
    }

    .reset-button-container {
      margin-top: 10px;

      button {
        color: ${({ theme }) => theme.colorPalette.red600};
      }
    }

    .export-buttons-container,
    .reset-button-container {
      margin-top: 1rem;
      display: flex;

      button {
        width: 100%;
        height: 40px;
        text-align: center;

        span {
          width: 100%;
          font-size: ${({ theme }) => theme.fontSize.base};
        }
      }
    }

    .table-container {
      overflow-x: auto;

      table {
        width: 100%;
        margin: 1em 0;
        background: #ffffff;
      }

      table tbody {
        width: auto;
        position: relative;
        overflow-x: auto;
      }

      table td,
      table th {
        padding: 0.625em;
        line-height: 1.5em;
        border-bottom: 1px solid #bbbbbb;
        box-sizing: border-box;
        overflow-x: hidden;
        overflow-y: auto;
        white-space: nowrap;
        width: auto;
      }

      table th {
        text-align: left;
        background: #3a4e5b;
        color: ${({ theme }) => theme.colorPalette.white};
      }

      table tr:nth-child(odd) {
        background: rgba(0, 0, 0, 0.07);
      }
    }

    .export-buttons-container {
      margin-top: 20px;

      button {
        &:first-of-type {
          margin-right: 10px;
        }
      }
    }
    &.compact {
      .expenses-list {
        max-height: 500px;
        height: 100%;
        overflow-y: scroll;

        li {
          height: unset;
          flex-direction: column;

          .expense-name {
            margin-right: 0;
            width: 100%;
          }

          .remove-button {
            margin-top: 0.5rem;
            margin-left: 0;
            width: 100%;
          }

          .remove-button-text {
            margin-right: 10px;
            display: block;
          }

          .input-container {
            margin-right: 0;
            width: 100%;
          }

          .inputs-container {
            padding: 10px 0;
            flex-direction: column;
            width: 100%;

            > div {
              flex-direction: column;
              width: 100%;
            }
          }
        }
      }
    }

    @media screen and (max-width: 800px) {
      .expenses-list {
        li {
          height: unset;
          flex-direction: column;

          .expense-name {
            margin-right: 0;
            width: 100%;
          }

          .remove-button {
            margin-top: 0.5rem;
            margin-left: 0;
            width: 100%;
          }

          .remove-button-text {
            margin-right: 10px;
            display: block;
          }

          .input-container {
            margin-right: 0;
            width: 100%;
          }

          .inputs-container {
            padding: 10px 0;
            flex-direction: column;
            width: 100%;

            > div {
              flex-direction: column;
              width: 100%;
            }
          }
        }
      }
    }
  }
`;
